import React, { useState } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, parseISO, isBefore, addMonths, addYears } from 'date-fns';
import RecurringExpenseModal from './RecurringExpenseModal';

const { FiPlus, FiRefreshCw, FiArrowRight, FiTrash2, FiClock, FiCheckCircle } = FiIcons;

const RecurringExpenseManager = () => {
  const { data, addRecord, updateRecord, deleteRecord } = useStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const schedules = data.recurringExpenses || [];
  const today = new Date();

  const handlePostTransaction = (schedule) => {
    // 1. Create the actual transaction
    addRecord('transactions', {
      propertyId: schedule.propertyId,
      date: format(today, 'yyyy-MM-dd'),
      type: 'Expense',
      category: schedule.category,
      amount: schedule.amount,
      description: `[Auto] ${schedule.description}`,
      tags: [...schedule.tags, 'Recurring']
    });

    // 2. Update the next due date
    let nextDate = parseISO(schedule.nextDueDate);
    if (schedule.frequency === 'Monthly') nextDate = addMonths(nextDate, 1);
    else if (schedule.frequency === 'Quarterly') nextDate = addMonths(nextDate, 3);
    else if (schedule.frequency === 'Annually') nextDate = addYears(nextDate, 1);

    updateRecord('recurringExpenses', schedule.id, {
      nextDueDate: format(nextDate, 'yyyy-MM-dd')
    });
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-xl font-black text-gray-900">Expense Automation</h3>
          <p className="text-xs text-gray-500 font-medium">Manage fixed costs and recurring bills.</p>
        </div>
        <button 
          onClick={() => { setEditingItem(null); setIsModalOpen(true); }}
          className="flex items-center px-4 py-2 bg-indigo-50 text-indigo-700 rounded-xl hover:bg-indigo-100 transition font-black text-[10px] uppercase tracking-widest"
        >
          <SafeIcon icon={FiPlus} className="mr-2" /> New Schedule
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {schedules.map(s => {
          const isDue = isBefore(parseISO(s.nextDueDate), today);
          const property = data.properties.find(p => p.id === s.propertyId);

          return (
            <div key={s.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-all flex flex-col md:flex-row md:items-center justify-between gap-6 group">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isDue ? 'bg-amber-100 text-amber-600' : 'bg-indigo-50 text-indigo-600'}`}>
                  <SafeIcon icon={FiRefreshCw} size={20} className={isDue ? 'animate-spin-slow' : ''} />
                </div>
                <div>
                  <h4 className="font-black text-gray-900">{s.description}</h4>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">
                    {property?.name || 'Shared Portfolio'} • {s.frequency}
                  </p>
                </div>
              </div>

              <div className="flex-1 flex flex-wrap gap-2 px-4 border-l border-gray-50">
                {s.tags.map(tag => (
                  <span key={tag} className="px-2 py-0.5 bg-gray-50 text-gray-500 rounded text-[9px] font-black uppercase">#{tag}</span>
                ))}
              </div>

              <div className="flex items-center gap-8">
                <div className="text-right">
                  <p className="text-lg font-black text-gray-900">${s.amount}</p>
                  <p className={`text-[10px] font-bold uppercase ${isDue ? 'text-amber-600 animate-pulse' : 'text-gray-400'}`}>
                    Next: {format(parseISO(s.nextDueDate), 'MMM dd')}
                  </p>
                </div>
                
                <div className="flex gap-2">
                  {isDue && (
                    <button 
                      onClick={() => handlePostTransaction(s)}
                      className="px-4 py-2 bg-green-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-green-700 transition flex items-center gap-2"
                    >
                      <SafeIcon icon={FiCheckCircle} /> Post to Ledger
                    </button>
                  )}
                  <button 
                    onClick={() => deleteRecord('recurringExpenses', s.id)}
                    className="p-2 text-gray-300 hover:text-red-500 transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        
        {schedules.length === 0 && (
          <div className="py-20 text-center border-2 border-dashed border-gray-100 rounded-3xl bg-white">
            <SafeIcon icon={FiClock} size={48} className="text-gray-100 mx-auto mb-4" />
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">No recurring schedules configured</p>
          </div>
        )}
      </div>

      <RecurringExpenseModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={(item) => addRecord('recurringExpenses', item)}
        properties={data.properties}
        initialData={editingItem}
      />
    </div>
  );
};

export default RecurringExpenseManager;